// `/admin/international-withdrawals` (`spec/30-scalefy.md` §5.7, WEB-135): fila de saques
// do Saldo internacional em USDT, pagos à mão por um admin. Abre em "Na fila", o mais
// antigo primeiro (ordem da API).
//
// Rota-PAI: desenha a fila e, por cima, o `RouteDrawer` com o `<Outlet/>` da rota `$id`
// (`spec/24` §1 decisão 5). Fechar o drawer volta pra cá preservando o `search`.
//
// O `403 mfa_required` do `claim` não é tratado aqui: `api/client.ts` abre o `StepUpDialog`
// e reexecuta a chamada (`spec/17` §4.3).

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Outlet, useMatch, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { adminMeQueryOptions, adminsQueryOptions } from '../api/queries/admin-system.ts';
import { ErrorState } from '../components/app/error-state.tsx';
import { Btn } from '../components/kit/btn.tsx';
import { Mono } from '../components/kit/mono.tsx';
import { Notice } from '../components/kit/notice.tsx';
import { Panel } from '../components/kit/panel.tsx';
import { RouteDrawer } from '../components/kit/route-drawer.tsx';
import { problemOf } from '../features/admin-international-withdrawals/errors.ts';
import {
  claimIntlWithdrawal,
  cryptoSettingsQueryOptions,
  INTL_ACTION_INVALIDATES,
  type IntlListItem,
  type IntlStatus,
  intlListQueryOptions,
} from '../features/admin-international-withdrawals/queries.ts';
import {
  CryptoStateBanner,
  IntlWithdrawalsQueue,
  WalletNeeds,
} from '../features/admin-international-withdrawals/queue.tsx';
import {
  adminNameMap,
  QUEUE_STATUS,
  STATUS_IDS,
} from '../features/admin-international-withdrawals/rules.ts';
import { CRYPTO_SETTINGS_ANCHOR } from '../features/admin-system/crypto-withdrawals-settings.tsx';

/** A aba é o `status` da URL: link direto pra "em pagamento" ou "pagos" continua valendo. */
const searchSchema = z.object({
  status: z.enum(STATUS_IDS).default(QUEUE_STATUS).catch(QUEUE_STATUS),
  seller_id: z.string().optional(),
});

/** Gaveta mais larga que a padrão (420): um endereço de 42 caracteres cabe em duas linhas. */
const DRAWER_WIDTH = 480;

function AdminInternationalWithdrawals() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { status, seller_id: sellerId } = Route.useSearch();

  const list = useInfiniteQuery(
    intlListQueryOptions({ status, ...(sellerId === undefined ? {} : { seller_id: sellerId }) }),
  );
  const settings = useQuery(cryptoSettingsQueryOptions());
  const me = useQuery(adminMeQueryOptions());
  const admins = useQuery(adminsQueryOptions());

  const search = (next: IntlStatus = status) => ({
    status: next,
    ...(sellerId === undefined ? {} : { seller_id: sellerId }),
  });

  const open = (withdrawal: IntlListItem): void => {
    void navigate({
      to: '/admin/international-withdrawals/$id',
      params: { id: withdrawal.id },
      search: search(),
    });
  };

  const claim = useMutation({
    mutationFn: (withdrawal: IntlListItem) => claimIntlWithdrawal(withdrawal.id),
    // Assumiu: o próximo passo é pagar, e quem tem o valor e o endereço é o detalhe.
    onSuccess: (_result, withdrawal) => {
      open(withdrawal);
    },
    onSettled: async () => {
      await Promise.all(
        INTL_ACTION_INVALIDATES.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
    },
  });

  const rows = list.data?.pages.flatMap((page) => page.data) ?? [];
  // `totals` vem na MESMA transação da página: a última é a mais fresca.
  const totals = list.data?.pages.at(-1)?.totals;
  const detail = useMatch({ from: '/admin/international-withdrawals/$id', shouldThrow: false });
  const claimProblem =
    claim.variables === undefined
      ? undefined
      : problemOf(claim.error, { network: claim.variables.network });

  return (
    <>
      {settings.data !== undefined && (
        <CryptoStateBanner
          settings={settings.data}
          onOpenSettings={() => {
            void navigate({ to: '/admin/system', hash: CRYPTO_SETTINGS_ANCHOR });
          }}
        />
      )}
      {/* Falhar calado aqui faria o admin tentar assumir sem saber que está pausado. */}
      {settings.isError && (
        <ErrorState
          error={settings.error}
          onRetry={() => {
            void settings.refetch();
          }}
        />
      )}

      {claimProblem !== undefined && claim.variables !== undefined && (
        <div role="alert">
          <Notice
            tone="bad"
            title={`${claim.variables.seller.display_name}: ${claimProblem.title}`}
            action={
              <Btn
                kind="card"
                small
                onClick={() => {
                  if (claim.variables !== undefined) {
                    open(claim.variables);
                  }
                }}
              >
                Abrir o pedido
              </Btn>
            }
          >
            {claimProblem.body}
            <span className="data-value mt-1 block break-all text-11 text-faint">
              {claimProblem.ref}
            </span>
          </Notice>
        </div>
      )}

      <WalletNeeds totals={totals} />

      <Panel>
        <IntlWithdrawalsQueue
          rows={rows}
          status={status}
          onStatusChange={(next) => {
            void navigate({ to: '/admin/international-withdrawals', search: search(next) });
          }}
          totals={totals}
          isLoading={list.isPending}
          error={list.error}
          onRetry={() => {
            void list.refetch();
          }}
          hasMore={list.hasNextPage}
          isLoadingMore={list.isFetchingNextPage}
          onLoadMore={() => {
            void list.fetchNextPage();
          }}
          onSelect={open}
          onClaim={(withdrawal) => {
            claim.mutate(withdrawal);
          }}
          claimingId={claim.isPending ? claim.variables.id : undefined}
          {...(detail === undefined ? {} : { selectedId: detail.params.id })}
          {...(sellerId === undefined ? {} : { sellerId })}
          onSellerChange={(next) => {
            void navigate({
              to: '/admin/international-withdrawals',
              search: { status, ...(next === undefined ? {} : { seller_id: next }) },
            });
          }}
          meId={me.data?.id}
          adminNames={adminNameMap(admins.data)}
        />
      </Panel>

      <RouteDrawer
        open={detail !== undefined}
        onClose={() => {
          void navigate({ to: '/admin/international-withdrawals', search: search() });
        }}
        title="Saque em USDT"
        width={DRAWER_WIDTH}
        {...(detail === undefined ? {} : { sub: <Mono>{detail.params.id}</Mono> })}
      >
        <Outlet />
      </RouteDrawer>
    </>
  );
}

export const Route = createFileRoute('/admin/international-withdrawals')({
  validateSearch: searchSchema,
  component: AdminInternationalWithdrawals,
});
